import { useState } from 'react';
import { PlayCircle, RefreshCw, Loader2, CheckCircle, Filter } from 'lucide-react';
import { usePatrolRounds, usePatrolCheckpoints } from '../hooks';
import { PatrolRoundCard, PatrolProgress } from '../components';
import { useI18n } from '@/shared/i18n';
import { useToast } from '@/app/providers';
import { ShiftRequiredGuard } from '@/modules/guard/ui/components';
import type { PatrolStatus } from '@yawi/types';

const STATUSES: PatrolStatus[] = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'INCOMPLETE'];

export const PatrolRoundsPage = () => (
  <ShiftRequiredGuard>
    <PatrolRoundsContent />
  </ShiftRequiredGuard>
);

const PatrolRoundsContent = () => {
  const { t } = useI18n();
  const toast = useToast();
  const {
    rounds,
    pagination,
    currentRound,
    loading,
    error,
    params,
    filterByStatus,
    goToPage,
    startRound,
    scanCheckpoint,
    completeRound,
    refresh,
  } = usePatrolRounds();

  const { checkpoints } = usePatrolCheckpoints();

  const [actionLoading, setActionLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRound, setSelectedRound] = useState<string | null>(null);

  const handleStartRound = async () => {
    setActionLoading(true);
    try {
      await startRound();
      toast.success(t('patrol.roundStarted'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteRound = async () => {
    if (!currentRound) return;
    setActionLoading(true);
    try {
      await completeRound(currentRound.id);
      toast.success(t('patrol.roundCompleted'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleScanCheckpoint = async (checkpointId: string) => {
    if (!currentRound) return;
    const checkpoint = checkpoints.find(c => c.id === checkpointId);
    if (!checkpoint) return;

    setActionLoading(true);
    try {
      await scanCheckpoint(currentRound.id, { qrContent: checkpoint.qrCode });
      toast.success(t('patrol.checkpointScanned'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">{t('patrol.title')}</h1>
        <div className="flex items-center gap-2">
          {currentRound ? (
            <button
              onClick={handleCompleteRound}
              disabled={actionLoading}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              {t('patrol.completeRound')}
            </button>
          ) : (
            <button
              onClick={handleStartRound}
              disabled={actionLoading || checkpoints.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <PlayCircle className="w-4 h-4" />
              )}
              {t('patrol.startRound')}
            </button>
          )}
        </div>
      </div>

      {/* Current Round Progress */}
      {currentRound && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-3">{t('patrol.currentRound')}</h2>
          <PatrolProgress
            round={currentRound}
            checkpoints={checkpoints}
            onScanCheckpoint={handleScanCheckpoint}
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-3 py-2 border rounded-lg transition-colors ${
            showFilters ? 'bg-primary-50 border-primary-300 text-primary-700' : 'border-slate-300 text-slate-700 hover:bg-slate-50'
          }`}
        >
          <Filter className="w-4 h-4" />
          {t('common.filter')}
        </button>
        <button
          onClick={refresh}
          className="px-3 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {showFilters && (
        <div className="mb-4 p-4 bg-slate-50 rounded-lg">
          <label className="block text-sm font-medium text-slate-700 mb-2">{t('patrol.filterByStatus')}</label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => filterByStatus(undefined)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                !params.status ? 'bg-primary-600 text-white' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100'
              }`}
            >
              {t('common.all')}
            </button>
            {STATUSES.map((status) => (
              <button
                key={status}
                onClick={() => filterByStatus(status)}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  params.status === status ? 'bg-primary-600 text-white' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100'
                }`}
              >
                {t(`patrol.status.${status.toLowerCase()}`)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      )}

      {/* Rounds History */}
      {!loading && (
        <>
          <h2 className="text-lg font-semibold text-slate-800 mb-3">{t('patrol.roundsHistory')}</h2>
          <div className="space-y-3">
            {rounds.map((round) => (
              <PatrolRoundCard
                key={round.id}
                round={round}
                onClick={() => setSelectedRound(round.id === selectedRound ? null : round.id)}
              />
            ))}

            {rounds.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <p>{t('patrol.noRounds')}</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => goToPage(pagination.page - 1)}
            disabled={!pagination.hasPreviousPage}
            className="px-3 py-1 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
          >
            {t('common.previous')}
          </button>
          <span className="px-4 py-1 text-sm text-slate-600">
            {pagination.page} / {pagination.totalPages}
          </span>
          <button
            onClick={() => goToPage(pagination.page + 1)}
            disabled={!pagination.hasNextPage}
            className="px-3 py-1 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
          >
            {t('common.next')}
          </button>
        </div>
      )}
    </div>
  );
};
